import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCreatePickupLocation, useUpdatePickupLocation } from '@/api/domains/pickup-locations';
import type { PickupLocation } from '@/api/types/pickups';
import { FormSwitchField, FormTextField } from '@/components/form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { applyApiErrors, toApiProblem } from '@/lib/errors';

import { pickupLocationSchema, type PickupLocationFormValues } from '../schemas';

const EMPTY: PickupLocationFormValues = {
  name: '',
  street: '',
  house_number: '',
  flat_number: null,
  postal_code: '',
  city: '',
  description: null,
  is_default: false,
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location: PickupLocation | null;
};

export function PickupLocationFormDialog({ open, onOpenChange, location }: Props) {
  const { t } = useTranslation('settings');
  const tCommon = useTranslation('common').t;
  const create = useCreatePickupLocation();
  const update = useUpdatePickupLocation(location?.id ?? 0);

  const form = useForm<PickupLocationFormValues>({
    resolver: zodResolver(pickupLocationSchema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(
      location
        ? {
            name: location.name,
            street: location.street,
            house_number: location.house_number,
            flat_number: location.flat_number,
            postal_code: location.postal_code,
            city: location.city,
            description: location.description,
            is_default: location.is_default,
          }
        : EMPTY,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, location]);

  const onSubmit = (values: PickupLocationFormValues) => {
    const mutation = location ? update : create;
    mutation.mutate(values, {
      onSuccess: () => {
        toast.success(location ? t('pickupLocations.updated') : t('pickupLocations.created'));
        onOpenChange(false);
      },
      onError: (error) => applyApiErrors(form, toApiProblem(error)),
    });
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {location ? t('pickupLocations.editTitle') : t('pickupLocations.addTitle')}
          </DialogTitle>
          <DialogDescription>{t('pickupLocations.formDescription')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4 md:grid-cols-2">
            <FormTextField<PickupLocationFormValues>
              name="name"
              label={t('pickupLocations.fields.name')}
              required
              className="md:col-span-2"
            />
            <FormTextField<PickupLocationFormValues>
              name="street"
              label={t('pickupLocations.fields.street')}
              required
              className="md:col-span-2"
              autoComplete="address-line1"
            />
            <FormTextField<PickupLocationFormValues>
              name="house_number"
              label={t('pickupLocations.fields.houseNumber')}
              required
            />
            <FormTextField<PickupLocationFormValues>
              name="flat_number"
              label={t('pickupLocations.fields.flatNumber')}
            />
            <FormTextField<PickupLocationFormValues>
              name="postal_code"
              label={t('pickupLocations.fields.postalCode')}
              required
              placeholder="00-001"
              autoComplete="postal-code"
            />
            <FormTextField<PickupLocationFormValues>
              name="city"
              label={t('pickupLocations.fields.city')}
              required
              autoComplete="address-level2"
            />
            {/* One-time pickups are Poland-only, so the dropdown offers only PL. */}
            <div className="flex flex-col gap-2 md:col-span-2">
              <Label>{t('pickupLocations.fields.country')}</Label>
              <Select value="PL">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PL">{t('pickupLocations.poland')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FormTextField<PickupLocationFormValues>
              name="description"
              label={t('pickupLocations.fields.description')}
              className="md:col-span-2"
            />
            <FormSwitchField<PickupLocationFormValues>
              name="is_default"
              label={t('pickupLocations.fields.isDefault')}
              className="md:col-span-2"
            />

            <DialogFooter className="md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {tCommon('actions.cancel')}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? tCommon('actions.saving') : tCommon('actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
